/**
 * shadcn/ui-style wrapper around Radix ``@radix-ui/react-context-menu``
 * (REQ-UI-028 — shadcn/ui component layer; ADR-024 §5).
 *
 * Mirrors the existing shadcn components' conventions (the ``cn`` merge
 * helper, ``forwardRef`` + ``displayName``, Meridian design-system tokens
 * from ``shared/design-system.md`` — ``bg-bg-overlay`` surface,
 * ``border-border``, ``text-text-primary`` body, ``bg-bg-elevated``
 * focus/hover, ``text-text-muted`` for the disabled hint). The first
 * consumer is the list panels' Phase-2 "Select on Map" affordance (the
 * 2026-06-09 two-slot selection-sync redesign).
 *
 * # Why Radix ContextMenu (not the dropdown-menu)
 *
 * Radix's ``ContextMenu`` primitive gives, for free, the three things the
 * "Select on Map" affordance needs and a hand-rolled menu would have to
 * re-implement:
 *
 *   1. **Right-click** (``contextmenu`` event) opens the menu at the
 *      pointer.
 *   2. **Keyboard parity** — the context-menu key / ``Shift+F10`` opens it
 *      on the focused row, and arrow-key navigation + ``Enter`` activate
 *      items (REQ-UI-059 — operators work the lists by keyboard).
 *   3. **Accessibility** — the trigger advertises ``aria-haspopup`` and the
 *      content is a focus-trapped ``role="menu"`` with managed roving
 *      focus.
 *
 * This wrapper exposes only the parts the panels use (``Root`` / ``Trigger``
 * / ``Content`` / ``Item``); the unused Radix parts (checkbox/radio/sub
 * menus, groups, labels) are intentionally not re-exported to keep the
 * surface small until a second consumer needs them.
 *
 * Cross-references:
 *   - REQ-UI-028 (shadcn/ui component layer).
 *   - REQ-UI-059 (keyboard navigation / context-menu key + Shift+F10).
 *   - REQ-UI-096 (cross-pane navigation / "Select on Map").
 *   - ADR-024 §5 (selection bus); shared/design-system.md (tokens).
 */
import * as React from "react";
import * as ContextMenuPrimitive from "@radix-ui/react-context-menu";
import { cn } from "@/lib/utils";

/**
 * The context-menu root. Wraps the trigger + portalled content; manages
 * open state in response to right-click / keyboard on the trigger subtree.
 */
export const ContextMenu = ContextMenuPrimitive.Root;

/**
 * The right-clickable region. Render the list row as
 * ``<ContextMenuTrigger asChild>`` so the row element itself is the
 * trigger (no wrapper div) — preserving the row's own ``role`` / tabindex /
 * click handlers. Radix wires the ``contextmenu`` + keyboard handlers onto
 * the child.
 */
export const ContextMenuTrigger = ContextMenuPrimitive.Trigger;

/**
 * The portalled menu surface. Styled as a Meridian overlay popover
 * (``bg-bg-overlay`` + ``border-border`` + ``shadow-lg``), portalled to
 * ``<body>`` so it escapes the list's ``overflow-y-auto`` clip and z-stack.
 */
export const ContextMenuContent = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Content>
>(function ContextMenuContent({ className, ...props }, ref) {
  return (
    <ContextMenuPrimitive.Portal>
      <ContextMenuPrimitive.Content
        ref={ref}
        className={cn(
          "z-50 min-w-[10rem] overflow-hidden rounded-md border border-border",
          "bg-bg-overlay p-1 text-text-primary shadow-lg",
          className,
        )}
        {...props}
      />
    </ContextMenuPrimitive.Portal>
  );
});

/**
 * A menu item. ``focus:bg-bg-elevated`` gives the roving-focus / hover
 * highlight; ``data-[disabled]`` (set by Radix when ``disabled``) dims it
 * and shows the not-allowed cursor — used for the "No map location" hint on
 * aggregator / unlocated rows.
 */
export const ContextMenuItem = React.forwardRef<
  React.ElementRef<typeof ContextMenuPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof ContextMenuPrimitive.Item>
>(function ContextMenuItem({ className, ...props }, ref) {
  return (
    <ContextMenuPrimitive.Item
      ref={ref}
      className={cn(
        "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5",
        "text-body-sm text-text-primary outline-none",
        "focus:bg-bg-elevated focus:text-text-primary",
        "data-[disabled]:pointer-events-none data-[disabled]:cursor-not-allowed data-[disabled]:text-text-disabled",
        className,
      )}
      {...props}
    />
  );
});
